import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../shared/LoadingScreen';

/**
 * ProtectedRoute Component
 *
 * Protects routes from unauthorized access based on authentication and role
 *
 * @param {Object} props
 * @param {React.Component} props.children - Component to render if authorized
 * @param {string} props.requiredRole - Required role ('trainer', 'client', or undefined for any authenticated user)
 * @param {boolean} props.requireEmailVerification - Whether to require email verification
 */
const ProtectedRoute = ({
  children,
  requiredRole = null,
  requireEmailVerification = false
}) => {
  const { currentUser, userProfile, loading } = useAuth();
  const location = useLocation();

  // Show loading screen while checking authentication
  if (loading) {
    return <LoadingScreen />;
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check email verification if required
  if (requireEmailVerification && !currentUser.emailVerified) {
    return <Navigate to="/verify-email" state={{ email: currentUser.email }} replace />;
  }

  // Check role if specified
  if (requiredRole && userProfile?.role !== requiredRole) {
    // Redirect to appropriate dashboard based on actual role
    const redirectPath = userProfile?.role === 'trainer'
      ? '/unique-physique/trainer-dashboard'
      : '/unique-physique/client-dashboard';

    return <Navigate to={redirectPath} replace />;
  }

  // User is authenticated and authorized
  return children;
};

/**
 * PublicRoute Component
 *
 * Routes that are only accessible when NOT logged in (login, signup, etc.)
 * Redirects to dashboard if already authenticated AND has a profile
 */
export const PublicRoute = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  // Allow authenticated users WITHOUT profiles to complete signup
  // This handles Google sign-in during signup flow
  if (currentUser && userProfile) {
    // User is fully set up with profile - redirect to dashboard
    const redirectPath = userProfile.role === 'trainer'
      ? '/unique-physique/trainer-dashboard'
      : '/unique-physique/client-dashboard';

    return <Navigate to={redirectPath} replace />;
  }

  // Allow access to signup if:
  // - Not authenticated at all
  // - Authenticated but no profile (completing Google signup)
  if (location.pathname === '/signup' && currentUser && !userProfile) {
    // Let them complete the signup process
    return children;
  }

  // For login page, redirect if already has profile
  if (currentUser && userProfile) {
    const redirectPath = userProfile.role === 'trainer'
      ? '/unique-physique/trainer-dashboard'
      : '/unique-physique/client-dashboard';

    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

export default ProtectedRoute;
